import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, ShoppingBag, Power } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { toast } from "sonner";
import {
  ADMIN_BADGE_OK,
  ADMIN_BADGE_OFF,
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";

const emptyForm = {
  name: "",
  price: 0,
  age_range: "",
  description: "",
  image_url: "",
  features: "",
  in_stock: true,
  bonus_months: 0,
  display_order: 0,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/products");
      if (res.ok) setProducts(await res.json());
      else toast.error("Impossible de charger les produits");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      price: item.price ?? 0,
      age_range: item.age_range || "",
      description: item.description || "",
      image_url: item.image_url || "",
      features: (item.features || []).join("\n"),
      in_stock: item.in_stock !== false,
      bonus_months: item.bonus_months ?? 0,
      display_order: item.display_order ?? 0,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Le nom est requis";
    if (form.price === "" || Number(form.price) < 0) {
      next.price = "Le prix doit être un nombre positif";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      price: Number(form.price) || 0,
      age_range: form.age_range.trim(),
      description: form.description.trim(),
      image_url: form.image_url.trim(),
      features: form.features
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      in_stock: form.in_stock !== false,
      bonus_months: Math.max(0, Number(form.bonus_months) || 0),
      display_order: Number(form.display_order) || 0,
      active: editing ? editing.active !== false : true,
    };

    try {
      const url = editing ? `/api/admin/products/${editing.id}` : "/api/admin/products";
      const method = editing ? "PUT" : "POST";
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editing ? "Produit mis à jour" : "Produit créé");
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    setTogglingId(item.id);
    const nextActive = item.active === false;
    try {
      const res = await authFetch(`/api/admin/products/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: nextActive }),
      });
      if (!res.ok) throw new Error("Erreur");
      toast.success(nextActive ? "Produit activé" : "Produit désactivé — retiré de la boutique");
      load();
    } catch {
      toast.error("Impossible de changer le statut");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/admin/products/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur");
      toast.success(`Produit « ${deleteTarget.name} » supprimé`);
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Impossible de supprimer");
    } finally {
      setDeleting(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <div>
      <AdminPageHeader
        icon={ShoppingBag}
        title="Produits boutique"
        description="Gérer les robots et kits affichés sur /boutique"
        action={
          <Button onClick={openCreate} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Nouveau produit
          </Button>
        }
      />

      {loading ? (
        <AdminLoading message="Chargement des produits..." />
      ) : products.length === 0 ? (
        <AdminEmptyState
          icon={ShoppingBag}
          title="Aucun produit"
          description="Ajoutez votre premier robot ou kit pour l'afficher dans la boutique."
          action={
            <Button onClick={openCreate} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau produit
            </Button>
          }
        />
      ) : (
        <AdminTable
          mobileCards={products.map((item) => (
            <AdminMobileCard
              key={item.id}
              title={item.name}
              subtitle={`${item.price} €${item.age_range ? ` · ${item.age_range}` : ""}`}
              onClick={() => openEdit(item)}
              badge={
                <Badge className={item.active !== false ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                  {item.active !== false ? "Actif" : "Inactif"}
                </Badge>
              }
              meta={[
                { label: "Stock", value: item.in_stock !== false ? "En stock" : "Rupture" },
              ]}
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(item)}
                    disabled={togglingId === item.id}
                    className={item.active === false ? "text-emerald-600" : "text-amber-600"}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} aria-label={`Supprimer ${item.name}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Produit</TableHead>
                <TableHead className={adminTableHeadClass}>Prix</TableHead>
                <TableHead className={adminTableHeadClass}>Âge</TableHead>
                <TableHead className={adminTableHeadClass}>Stock</TableHead>
                <TableHead className={adminTableHeadClass}>Ordre</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((item) => (
                <TableRow key={item.id} className={adminClickableRowClass} onClick={() => openEdit(item)}>
                  <TableCell>
                    <div className="flex items-center gap-3 min-w-0">
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt=""
                          className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="text-slate-900 font-medium truncate">{item.name}</p>
                        <p className="text-slate-500 text-xs truncate">
                          {item.bonus_months > 0
                            ? `+${item.bonus_months} mois e-learning`
                            : "Sans bonus e-learning"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-700 tabular-nums">{item.price} €</TableCell>
                  <TableCell className="text-slate-400">{item.age_range || "—"}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        item.in_stock !== false
                          ? ADMIN_BADGE_OK
                          : "bg-red-50 text-red-700"
                      }
                    >
                      {item.in_stock !== false ? "En stock" : "Rupture"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-400 tabular-nums">{item.display_order}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        item.active !== false
                          ? ADMIN_BADGE_OK
                          : "bg-slate-100 text-slate-500"
                      }
                    >
                      {item.active !== false ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(item)}
                      disabled={togglingId === item.id}
                      aria-label={item.active === false ? "Activer" : "Désactiver"}
                      className={item.active === false ? "text-emerald-600" : "text-amber-600"}
                    >
                      {togglingId === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(item)}
                      aria-label={`Supprimer ${item.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className={ADMIN_LABEL}>Nom *</Label>
              <Input
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                className={`${ADMIN_FIELD} ${errors.name ? "border-red-500" : ""}`}
              />
              {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={ADMIN_LABEL}>Prix (€) *</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.price}
                  onChange={(e) => updateField("price", e.target.value)}
                  className={`${ADMIN_FIELD} ${errors.price ? "border-red-500" : ""}`}
                />
                {errors.price && <p className="text-red-600 text-xs mt-1">{errors.price}</p>}
              </div>
              <div>
                <Label className={ADMIN_LABEL}>Tranche d&apos;âge</Label>
                <Input
                  value={form.age_range}
                  onChange={(e) => updateField("age_range", e.target.value)}
                  placeholder="6-8 ans"
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div>
              <Label className={ADMIN_LABEL}>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                className={ADMIN_FIELD}
                rows={3}
              />
            </div>
            <div>
              <Label className={ADMIN_LABEL}>Image (URL)</Label>
              <Input
                value={form.image_url}
                onChange={(e) => updateField("image_url", e.target.value)}
                placeholder="/images/offers/..."
                className={ADMIN_FIELD}
              />
            </div>
            <div>
              <Label className={ADMIN_LABEL}>Caractéristiques (une par ligne)</Label>
              <Textarea
                value={form.features}
                onChange={(e) => updateField("features", e.target.value)}
                placeholder={"200+ pièces\nProgrammation visuelle"}
                className={ADMIN_FIELD}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={ADMIN_LABEL}>Mois e-learning offerts</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.bonus_months}
                  onChange={(e) => updateField("bonus_months", e.target.value)}
                  className={ADMIN_FIELD}
                />
              </div>
              <div>
                <Label className={ADMIN_LABEL}>Ordre d&apos;affichage</Label>
                <Input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => updateField("display_order", e.target.value)}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.in_stock}
                onChange={(e) => updateField("in_stock", e.target.checked)}
                className="rounded border-slate-600"
              />
              En stock
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className={BTN_OUTLINE}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button onClick={save} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title={`Supprimer « ${deleteTarget?.name || ""} » ?`}
        description={`« ${deleteTarget?.name || "Ce produit"} » sera définitivement retiré de la boutique.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
